// AquaFlow reference test: official EPANET example networks run unchanged in the app (WASM) vs. native OWA-EPANET
import { solveRef, simulate } from './aquaNet';
import { inpToProject } from './inpImport';

export const REF_NETS = ['Net1', 'Net3'];
// file units are US (GPM, psi, ft) -> SI for display and tolerances
const PSI = 0.0689476, FT = 0.3048, GPM = 0.0630902;
export const TOL = { p: 0.05, lvl: 0.03, qRel: 0.01, qAbs: 0.1 };

const maxDev = (a, b, k) => Object.keys(a).reduce((m, id) => Math.max(m, ...a[id].map((v, i) => Math.abs((v - (b[id]?.[i] ?? NaN)) * k))), 0);
const span = (arr) => [Math.min(...arr), Math.max(...arr)];

function compare(ref, got) {
  const sameHours = JSON.stringify(ref.hours) === JSON.stringify(got.hours);
  const dP = maxDev(ref.p, got.p, PSI);
  const all = (o) => Object.values(o).flat();
  const tanks = Object.keys(ref.tank).map((id) => {
    const r = ref.tank[id].map((v) => v * FT), g = (got.tank[id] || []).map((v) => v * FT);
    const d = Math.max(...r.map((v, i) => Math.abs(v - (g[i] ?? NaN))));
    return { id, ref: span(r), got: span(g), end: [r[r.length - 1], g[g.length - 1]], d, ok: d <= TOL.lvl };
  });
  const pumps = Object.keys(ref.pump).map((id) => {
    const r = ref.pump[id], g = got.pump[id] || [];
    const d = Math.max(...r.map(([q], i) => Math.abs(q - (g[i]?.[0] ?? NaN)) * GPM));
    const tol = Math.max(TOL.qAbs, TOL.qRel * Math.max(...r.map(([q]) => Math.abs(q * GPM))));
    const on = (x) => x.filter(([, s]) => s > 0).length, st = r.every(([, s], i) => (s > 0) === ((g[i]?.[1] ?? 0) > 0));
    const avg = (x) => x.reduce((s, [q]) => s + q * GPM, 0) / Math.max(1, x.length);
    return { id, ref: avg(r), got: avg(g), onRef: on(r), onGot: on(g), d, tol, ok: d <= tol && st, st };
  });
  const p = { n: Object.keys(ref.p).length * ref.hours.length, d: dP, ok: dP <= TOL.p, ref: span(all(ref.p)).map((v) => v * PSI), got: span(all(got.p)).map((v) => v * PSI) };
  return { sameHours, hours: ref.hours.length, p, tanks, pumps, ok: sameHours && p.ok && tanks.every((x) => x.ok) && pumps.every((x) => x.ok) };
}

// load bundled files (precached by the service worker -> works offline)
export async function runRefTest(name) {
  const [inp, ref] = await Promise.all([fetch(`/aqua-ref/${name}.inp`).then((r) => r.text()), fetch('/aqua-ref/reference.json').then((r) => r.json())]);
  const t0 = performance.now();
  const res = await solveRef(inp);
  if (res.error) return { name, error: res.error };
  return { name, ver: res.ver, refEngine: ref.engine, counts: res.ref.counts, ms: Math.round(performance.now() - t0), at: Date.now(), ...compare(ref.nets[name], res.ref) };
}

// GISS model check: the INP imported into GISS fittings/pipes (pumps, tanks, controls) and solved by AquaFlow at 0 h
export async function runGissRefTest(name, t) {
  const [inp, ref] = await Promise.all([fetch(`/aqua-ref/${name}.inp`).then((r) => r.text()), fetch('/aqua-ref/reference.json').then((r) => r.json())]);
  const pr = inpToProject(inp, { mode: 'local', center: { lat: 46, lng: 11 }, t0: true }, t);
  const res = await simulate({ fittings: pr.fittings, pipes: pr.pipes });
  if (res.error) return { name, error: res.error };
  const R = ref.nets[name], byInp = Object.fromEntries(pr.fittings.filter((f) => f.type === 'node').map((f) => [f.inpId, f.id]));
  const rows = Object.keys(R.p).map((id) => ({ id, ref: R.p[id][0] * PSI, got: res.nodes[byInp[id]]?.bar })).filter((x) => x.got != null);
  const d = Math.max(...rows.map((x) => Math.abs(x.ref - x.got)));
  const pumps = (res.pumps || []).map((u) => { const f = pr.fittings.find((x) => x.id === u.fid); const r0 = R.pump[f.inpId]?.[0]; return { id: f.inpId, ref: r0 ? r0[0] * GPM : 0, got: u.q, ok: Math.abs((r0 ? r0[0] * GPM : 0) - u.q) <= Math.max(TOL.qAbs, TOL.qRel * Math.abs(r0?.[0] * GPM || 0)) }; });
  return { name, n: rows.length, of: Object.keys(R.p).length, d, ok: d <= TOL.p && rows.length === Object.keys(R.p).length && pumps.every((x) => x.ok), pumps, miss: pr.miss };
}
